import { type QueryClient, queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { z } from "zod";
import type { ActionDef, QueryDef } from "@/server/lib/module";
import type { ApiModules } from "@/server/modules";

/**
 * Cliente tipado da API (/api/v1). Os tipos vêm das definições do servidor
 * (só `import type` — nada do servidor entra no bundle do navegador).
 *
 *  - `serverAction(módulo, nome)`: função com a MESMA assinatura da regra no
 *    servidor. FormData vira multipart; o resto vai como JSON.
 *  - `apiQuery(módulo, nome, input)`: opções do TanStack Query para leituras.
 *
 * Toda ação bem-sucedida refaz as consultas ativas (o equivalente ao
 * `revalidatePath` + `router.refresh` do Next) e segue o `X-Redirect`.
 */

type Mods = ApiModules;
type Endpoints<M extends keyof Mods> = Mods[M]["endpoints"];
export type QueryName<M extends keyof Mods> = {
  [K in keyof Endpoints<M>]: Endpoints<M>[K] extends QueryDef ? K : never;
}[keyof Endpoints<M>] &
  string;
type QueryIn<M extends keyof Mods, K extends keyof Endpoints<M>> =
  Endpoints<M>[K] extends QueryDef<infer I> ? z.input<I> : never;
export type QueryOut<M extends keyof Mods, K extends keyof Endpoints<M>> =
  Endpoints<M>[K] extends QueryDef<z.ZodType, infer R> ? R : never;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

/**
 * Desfaz a serialização do servidor (`jsonReplacer`): "$D<ISO>" vira Date e
 * "$$..." volta a "$...". Assim Date continua Date e string continua string.
 */
export function parseApiJson(text: string): unknown {
  if (!text) return null;
  return JSON.parse(text, (_k, v) => {
    if (typeof v !== "string" || v[0] !== "$") return v;
    if (v[1] === "$") return v.slice(1);
    if (v[1] === "D") return new Date(v.slice(2));
    return v;
  });
}

let queryClientRef: QueryClient | null = null;
let navigateRef: ((to: string) => void) | null = null;

/** Ligado em main.tsx. */
export function bindApi(qc: QueryClient, navigate: (to: string) => void) {
  queryClientRef = qc;
  navigateRef = navigate;
}

const kebab = (name: string) =>
  name
    .replace(/Action$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();

async function handle(res: Response): Promise<unknown> {
  const text = await res.text();
  const data = parseApiJson(text);
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string };
    if (res.status === 401 && typeof window !== "undefined" && !location.pathname.startsWith("/entrar")) {
      navigateRef?.(`/entrar?de=${encodeURIComponent(location.pathname)}`);
    }
    throw new ApiError(res.status, err.error ?? "Erro inesperado. Tente de novo.", err.code);
  }
  return data;
}

/** Opções de uma ação: leituras puras não precisam refazer as consultas; troca de sessão limpa o cache. */
export type ActionOptions = {
  /** false = ação só de leitura: não invalida as consultas ativas. Padrão: true. */
  invalidate?: boolean;
  /** true = troca de usuário/sessão: descarta todo o cache (nada do usuário anterior sobra). */
  clearCache?: boolean;
};

export async function callAction(
  mod: string,
  name: string,
  args: unknown[],
  options: ActionOptions = {},
): Promise<unknown> {
  const formIdx = args.findIndex((a) => a instanceof FormData);
  let body: BodyInit;
  const headers: Record<string, string> = { "X-App-Request": "1" };
  if (formIdx >= 0) {
    const form = new FormData();
    const original = args[formIdx] as FormData;
    for (const [k, v] of original.entries()) form.append(k, v);
    form.set("__args", JSON.stringify(args.map((a, i) => (i === formIdx ? "$form" : (a ?? null)))));
    body = form;
  } else {
    body = JSON.stringify({ args: args.map((a) => a ?? null) });
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`/api/v1/${mod}/${kebab(name)}`, {
    method: "POST",
    body,
    headers,
    credentials: "same-origin",
  });
  const data = await handle(res);
  const redirect = res.headers.get("X-Redirect");
  if (options.clearCache) queryClientRef?.clear();
  else if (options.invalidate !== false) void queryClientRef?.invalidateQueries();
  if (redirect) navigateRef?.(redirect);
  return data;
}

/** Função cliente com a mesma assinatura da ação no servidor. */
export function serverAction<F extends (...args: never[]) => Promise<unknown>>(
  mod: string,
  name: string,
  options?: ActionOptions,
) {
  return ((...args: unknown[]) => callAction(mod, name, args, options)) as unknown as (
    ...args: Parameters<F>
  ) => Promise<Awaited<ReturnType<F>>>;
}

function toSearch(input: Record<string, unknown> | undefined): string {
  if (!input) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) for (const item of v) sp.append(k, String(item));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** Página que pediu outra tela (equivalente ao redirect() de server component). */
export class RedirectError extends Error {
  constructor(public to: string) {
    super(`redirect:${to}`);
  }
}

export async function getQuery(mod: string, name: string, input?: Record<string, unknown>): Promise<unknown> {
  const res = await fetch(`/api/v1/${mod}/${kebab(name)}${toSearch(input)}`, { credentials: "same-origin" });
  const data = await handle(res);
  const redirect = res.headers.get("X-Redirect");
  if (redirect) throw new RedirectError(redirect);
  return data;
}

export function apiQuery<M extends keyof Mods & string, K extends QueryName<M>>(mod: M, name: K, input: QueryIn<M, K>) {
  return queryOptions({
    queryKey: [mod, name, input] as const,
    queryFn: () => getQuery(mod, name, input as Record<string, unknown>) as Promise<QueryOut<M, K>>,
  });
}

export function useApiQuery<M extends keyof Mods & string, K extends QueryName<M>>(
  mod: M,
  name: K,
  input: QueryIn<M, K>,
) {
  return useQuery(apiQuery(mod, name, input));
}

export function useApiSuspenseQuery<M extends keyof Mods & string, K extends QueryName<M>>(
  mod: M,
  name: K,
  input: QueryIn<M, K>,
) {
  return useSuspenseQuery(apiQuery(mod, name, input));
}

export type { ActionDef, QueryDef };
